// ─────────────────────────────────────────────────────────────────────────────
// CRITICAL THINKING SCORING ENGINE
// ─────────────────────────────────────────────────────────────────────────────
import { CT_ITEMS, CT_DIMS } from "./questionBank.js";

// Norms: Global & Indian professional norms (n=7K+)
export const CT_NORMS = {
  INF: { mean:5.2, sd:1.4, n:680 },
  ASM: { mean:5.6, sd:1.3, n:680 },
  DED: { mean:5.4, sd:1.5, n:680 },
  INT: { mean:5.1, sd:1.6, n:680 },
  EVL: { mean:5.8, sd:1.2, n:680 },
  total: { mean:27.1, sd:6.1, n:680 },
};

// Sector benchmarks (mean total scores)
export const CT_SECTOR_NORMS = {
  consulting: { mean:32.4, sd:4.8, label:"Management Consulting" },
  legal:      { mean:31.8, sd:5.1, label:"Legal" },
  finance:    { mean:30.6, sd:5.3, label:"BFSI / Finance" },
  technology: { mean:28.9, sd:5.7, label:"Technology" },
  general:    { mean:27.1, sd:6.1, label:"All Professionals" },
};

export const CT_BANDS = [
  { min:36, max:40, label:"Exceptional",  color:"#059669", desc:"Top 5%. Exceptional critical thinking consistently applied across all dimensions." },
  { min:31, max:35, label:"Strong",       color:"#16a34a", desc:"Top 20%. Strong analytical rigour with minor gaps in specific dimensions." },
  { min:25, max:30, label:"Proficient",   color:"#0891b2", desc:"Above average. Solid critical thinking with room for development in assumptions and evaluation." },
  { min:18, max:24, label:"Developing",   color:"#d97706", desc:"Average range. Foundational skills present but inconsistent across dimensions." },
  { min:0,  max:17, label:"Foundation",   color:"#dc2626", desc:"Below average. Structured critical thinking development strongly recommended." },
];

function zToPercentile(z) {
  const a1=0.254829592,a2=-0.284496736,a3=1.421413741,a4=-1.453152027,a5=1.061405429,p=0.3275911;
  const sign = z < 0 ? -1 : 1;
  const x = Math.abs(z) / Math.sqrt(2);
  const t = 1 / (1 + p * x);
  const y = 1 - ((((a5*t+a4)*t+a3)*t+a2)*t+a1)*t*Math.exp(-x*x);
  return Math.round(((1 + sign * y) / 2) * 100);
}

export function scoreCriticalThinking(answers) {
  // answers: { "ct01": 0, "ct02": 1, ... }
  const byDim = {};
  CT_DIMS.forEach(d => { byDim[d.id] = { raw:0, total:0 }; });

  CT_ITEMS.forEach(item => {
    const ans = answers[item.id];
    byDim[item.dim].total++;
    if (ans !== null && ans !== undefined && ans === item.ans) {
      byDim[item.dim].raw++;
    }
  });

  const scales = {};
  const percentiles = {};
  let totalRaw = 0;

  CT_DIMS.forEach(d => {
    const raw = byDim[d.id].raw;
    totalRaw += raw;
    const norm = CT_NORMS[d.id];
    const z = (raw - norm.mean) / norm.sd;
    scales[d.id]    = raw;
    percentiles[d.id] = Math.min(99, Math.max(1, zToPercentile(z)));
  });

  const totalNorm = CT_NORMS.total;
  const totalZ    = (totalRaw - totalNorm.mean) / totalNorm.sd;
  const totalPct  = Math.min(99, Math.max(1, zToPercentile(totalZ)));
  const band      = CT_BANDS.find(b => totalRaw >= b.min && totalRaw <= b.max) || CT_BANDS[CT_BANDS.length-1];

  // Dimension strengths / development areas
  const dimRanked = CT_DIMS.map(d => ({
    ...d, raw:scales[d.id], pct:percentiles[d.id], outOf:8,
  })).sort((a,b) => b.pct - a.pct);

  return {
    scales,
    percentiles,
    totalRaw,
    totalPct,
    band,
    strengths:    dimRanked.slice(0,2).map(d=>d.label),
    development:  dimRanked.slice(-2).map(d=>d.label),
    dimRanked,
    ciIndex:      Math.round(totalPct),
  };
}
